// /dashboard/messages — the review archive (W36.0, spec §2.6 R6.4).
//
// 進入**獨立的回顧檔案頁**,**只收每日留言**,不混入紀念日 / 心願 — so this page
// lists nothing but `DailyMessage` rows. Adding another module's content here
// would defeat the point: the archive is the one place where a year of daily
// notes is legible as a year.
//
// Month grouping is derived from the date rather than fetched as sections, so
// paging stays a single keyset cursor (spec §5.2) while the UI still groups.
// Attribution is the last editor, per spec §2.2 R3.2.

import { useMemo, useState } from "react";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { ActionSheet } from "@/components/patterns/ActionSheet";
import { ConfirmDialog } from "@/components/patterns/ConfirmDialog";
import { MessageEditorSheet } from "./dashboard/components/MessageEditorSheet";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/primitives/EmptyState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { PageContainer, SectionStack, RowGap } from "@/components/layout";
import {
  useClearDailyMessage,
  useDailyMessageArchive,
  useSaveDailyMessage,
} from "@/hooks/useDashboard";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { formatLetterZh } from "@/lib/utils/date";
import { cn } from "@/lib/utils/cn";

function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

function monthLabel(iso: string): string {
  const [y, m] = iso.split("-");
  return `${y} 年 ${Number(m)} 月`;
}

export function DashboardMessagesPage() {
  const { data, isLoading, isError, refetch } = useDailyMessageArchive();

  // W52.0 D-10 — the write side existed the whole time (`putDailyMessage` /
  // `deleteDailyMessage` and their mutation hooks); the dashboard's editor
  // sheet was the only consumer. So today's line could be edited from the home
  // screen and yesterday's could not be edited anywhere.
  //
  // Both mutations are keyed by **date**, and `useMutation` takes that date at
  // hook-call time — so the hooks are created once against whichever row is
  // currently open. Passing "today" instead of the row's date is the failure
  // mode this feature has, and it is silent: the edit lands on another day and
  // nothing reports an error. `editing` holds the row, not just a flag.
  const [editing, setEditing] = useState<{ date: string; content: string } | null>(null);
  const [actionRow, setActionRow] = useState<{ date: string; content: string } | null>(null);
  const [deleting, setDeleting] = useState<{ date: string; content: string } | null>(null);

  const saveMessage = useSaveDailyMessage(editing?.date ?? "");
  // One delete hook, bound to whichever flow asked for it. `useClearDailyMessage`
  // takes its date at hook-call time, so it cannot be told which row to remove
  // per call — and 「編輯 → 清除」 inside the editor removes the row being
  // edited, which is a third caller of the same mutation.
  const deleteMessage = useClearDailyMessage((deleting ?? editing)?.date ?? "");

  const groups = useMemo(() => {
    const items = data?.items ?? [];
    const byMonth = new Map<string, typeof items>();
    for (const item of items) {
      const key = monthOf(item.date);
      const bucket = byMonth.get(key);
      if (bucket) bucket.push(item);
      else byMonth.set(key, [item]);
    }
    return Array.from(byMonth.entries()).map(([key, rows]) => ({
      key,
      label: monthLabel(rows[0]!.date),
      items: rows,
    }));
  }, [data?.items]);

  return (
    <div data-testid="dashboard-messages-page" className="flex flex-col pb-32">
      <PageHero
          // W52.0 D-9 — this route had **no** back control at all. It is a
          // screen reached from the dashboard's message block, and with no
          // trail the only way out was the browser's own back — which on a
          // phone is not something the app offers.
          crumbs={[
            { to: "/dashboard", label: "首頁", testId: "messages-back" },
            { label: "回顧" },
          ]}
          eyebrow={`共 ${data?.total ?? 0} 則`}
          title="回顧"
          subtitle={DASHBOARD_I18N.message.review}
        />
      <PageContainer vertical="default">
        <SectionStack density="default">
          {isLoading ? (
            <LoadingState count={4} shape="row" />
          ) : isError ? (
            <ErrorState
              title="載入回顧失敗"
              retryLabel="重試"
              onRetry={() => void refetch()}
            />
          ) : (data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title={DASHBOARD_I18N.message.reviewEmpty}
              description="每天寫一句, 這裡就會長出你們的時間。"
            />
          ) : (
            groups.map((group) => (
              <section key={group.key} data-testid={`message-month-${group.key}`}>
                <h2 className="mb-s-2 px-s-1 text-micro font-semibold uppercase tracking-wider text-ink-3">
                  {group.label}
                </h2>
                <RowGap density="default" as="ul" className="flex flex-col">
                  {group.items.map((item) => (
                    <li key={item.date}>
                      <Card data-testid={`archive-item-${item.date}`}>
                        <div className="p-s-4 flex flex-col gap-s-2">
                          <div className="flex items-center justify-between gap-s-2">
                            <span className="text-meta text-ink-3 tabular-nums">
                              {formatLetterZh(new Date(`${item.date}T00:00:00`))}
                            </span>
                            <div className="flex items-center gap-s-2">
                              <span
                                className={cn(
                                  "rounded-pill px-2 py-0.5 text-meta font-semibold",
                                  item.is_partner_authored
                                    ? "bg-partner-tint text-partner-strong"
                                    : "bg-you-tint text-you-strong",
                                )}
                                data-author={item.is_partner_authored ? "partner" : "self"}
                              >
                                {item.is_partner_authored
                                  ? DASHBOARD_I18N.message.authorPartner
                                  : DASHBOARD_I18N.message.authorSelf}
                              </span>
                              {/* 44×44pt target (apple-design: iOS default control
                                  size), a 16pt glyph on top. The glyph is the
                                  affordance; the target is the button. */}
                              <button
                                type="button"
                                data-testid={`message-row-actions-${item.date}`}
                                aria-label={`${formatLetterZh(
                                  new Date(`${item.date}T00:00:00`),
                                )} 的留言動作`}
                                onClick={() => setActionRow({ date: item.date, content: item.content })}
                                className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-black/5 transition-colors duration-quick"
                              >
                                <MoreHorizontal size={16} aria-hidden />
                              </button>
                            </div>
                          </div>
                          <p className="whitespace-pre-wrap text-body text-ink-1">
                            {item.content}
                          </p>
                        </div>
                      </Card>
                    </li>
                  ))}
                </RowGap>
              </section>
            ))
          )}
        </SectionStack>
      </PageContainer>

      {/* W52.0 D-10 — per-row actions, then the existing editor reused whole.
          Not a second editor: the 140-char limit, the clear action and the
          error surface all live in `MessageEditorSheet` already, and a copy
          would be a second set of rules for the same string.

          **The delete is a hard delete.** Messages have no `deleted_at`, so
          they cannot go through the soft-delete + restore flow every other
          module has. There is no bin for them, and the wording does not
          pretend otherwise. Making this undoable needs a backend change and is
          out of scope for a front-end round. */}
      <ActionSheet
        open={actionRow !== null}
        onClose={() => setActionRow(null)}
        title={actionRow ? formatLetterZh(new Date(`${actionRow.date}T00:00:00`)) : ""}
        testId="message-actions-sheet"
        actions={[
          {
            id: "edit",
            label: "編輯這則",
            leadingIcon: <Pencil size={18} aria-hidden />,
            testId: "message-action-edit",
            onClick: () => {
              if (actionRow) setEditing(actionRow);
              setActionRow(null);
            },
          },
          {
            id: "delete",
            label: "刪除這則",
            leadingIcon: <Trash2 size={18} aria-hidden />,
            tone: "danger",
            testId: "message-action-delete",
            onClick: () => {
              if (actionRow) setDeleting(actionRow);
              setActionRow(null);
            },
          },
        ]}
      />
      {/* A hard delete gets a confirmation, unlike the edit.
          This is the one irreversible control in the module and the one with
          no undo: messages have no `deleted_at`, so there is no bin to recover
          from and no toast to un-do it with. `apple-design` / HIG puts a
          confirmation on a destructive sheet action for exactly that reason —
          the sheet row is 「what you picked」, not 「what happens」. */}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="刪除這則留言？"
        description={
          deleting
            ? `${formatLetterZh(new Date(`${deleting.date}T00:00:00`))} 這則留言會被永久刪除，無法復原。`
            : ""
        }
        destructive
        confirmLabel="永久刪除"
        onConfirm={() => {
          if (deleting) void deleteMessage.mutateAsync();
          setDeleting(null);
        }}
      />
      <MessageEditorSheet
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        initialContent={editing?.content ?? ""}
        hasExisting={Boolean(editing?.content)}
        onSave={async (content) => {
          await saveMessage.mutateAsync(content);
        }}
        onClear={async () => {
          await deleteMessage.mutateAsync();
        }}
        isSaving={saveMessage.isPending}
        isClearing={deleteMessage.isPending}
      />
    </div>
  );
}
